import { ROUTES } from "@/constants/routes";

/**
 * Get the campaigns list path
 * @param clientId - Optional client ID for client-scoped routes
 * @returns Campaigns list path
 */
export function getCampaignsPath(clientId?: string): string {
  return clientId
    ? `/clients/${clientId}/${ROUTES.campaigns}`
    : `/${ROUTES.campaigns}`;
}

/**
 * Get the campaign detail path
 * @param clientId - Optional client ID for client-scoped routes
 * @param campaignId - Campaign ID
 * @returns Campaign detail path
 */
export function getCampaignDetailPath(
  clientId: string | undefined,
  campaignId: string
): string {
  const campaignsPath = getCampaignsPath(clientId);
  return `${campaignsPath}/${campaignId}`;
}

/**
 * Get the campaign edit path
 * @param clientId - Optional client ID for client-scoped routes
 * @param campaignId - Campaign ID
 * @returns Campaign edit path
 */
export function getCampaignEditPath(
  clientId: string | undefined,
  campaignId: string
): string {
  return `${getCampaignDetailPath(clientId, campaignId)}/${ROUTES.edit}`;
}

/**
 * Get the campaign integrations list path
 * @param clientId - Optional client ID for client-scoped routes
 * @param campaignId - Campaign ID
 * @returns Campaign integrations list path
 */
export function getCampaignIntegrationsPath(
  clientId: string | undefined,
  campaignId: string
): string {
  return `${getCampaignDetailPath(clientId, campaignId)}/${ROUTES.campaignIntegrations}`;
}

/**
 * Get the add campaign integration path
 * @param clientId - Optional client ID for client-scoped routes
 * @param campaignId - Campaign ID
 * @returns Add campaign integration path
 */
export function getAddCampaignIntegrationPath(
  clientId: string | undefined,
  campaignId: string
): string {
  return `${getCampaignIntegrationsPath(clientId, campaignId)}/${ROUTES.new}`;
}

/**
 * Get the campaign integration detail path
 * @param clientId - Optional client ID for client-scoped routes
 * @param campaignId - Campaign ID
 * @param clientIntegrationModuleId - Client integration module ID
 * @returns Campaign integration detail path
 */
export function getCampaignIntegrationDetailPath(
  clientId: string | undefined,
  campaignId: string,
  clientIntegrationModuleId: string
): string {
  return `${getCampaignIntegrationsPath(clientId, campaignId)}/${clientIntegrationModuleId}`;
}

/**
 * Get the edit campaign integration path
 * @param clientId - Optional client ID for client-scoped routes
 * @param campaignId - Campaign ID
 * @param clientIntegrationModuleId - Client integration module ID
 * @returns Edit campaign integration path
 */
export function getEditCampaignIntegrationPath(
  clientId: string | undefined,
  campaignId: string,
  clientIntegrationModuleId: string
): string {
  return `${getCampaignIntegrationDetailPath(clientId, campaignId, clientIntegrationModuleId)}/${ROUTES.edit}`;
}

/**
 * Gets the new campaign URL based on whether clientId is provided
 */
export function getNewCampaignUrl(clientId?: string): string {
  return `${getCampaignsPath(clientId)}/${ROUTES.new}`;
}
